import type { LayoutBlock } from '@web-studio/shared-schemas';
import { pickBlockField } from '../../../lib/localized';
import { parseMenuColumns, type MenuColumn, type MenuItem } from '../../../lib/parseMenuColumns';
import { menuItemHasContent } from '../menuEditHelpers';

export type HighlightDishRef = {
  item: MenuItem;
  columnIndex: number;
  itemIndex: number;
};

export type MarqueeChipRef = HighlightDishRef & {
  /** Cycle index within an expanded marquee group (0 = first pass). */
  cycle: number;
};

/**
 * Repeat dishes until the group has enough chips to fill a typical viewport.
 * Keeps tight gaps (not space-evenly) so the -50% dual-group loop looks continuous.
 */
export function expandMarqueeChips(
  dishes: HighlightDishRef[],
  minChips = 16,
): MarqueeChipRef[] {
  if (dishes.length === 0) return [];
  const cycles = Math.max(2, Math.ceil(minChips / dishes.length));
  const out: MarqueeChipRef[] = [];
  for (let cycle = 0; cycle < cycles; cycle++) {
    for (const dish of dishes) {
      out.push({ ...dish, cycle });
    }
  }
  return out;
}

export function readHighlightMenuSection(
  block: LayoutBlock,
  locale: string,
): {
  sectionTitle: string;
  sectionSubtitle: string;
  columns: MenuColumn[];
  dishes: HighlightDishRef[];
} {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const dishes = columns.flatMap((column, columnIndex) =>
    column.items
      .map((item, itemIndex) => ({ item, columnIndex, itemIndex }))
      .filter(({ item }) => menuItemHasContent(item)),
  );
  return { sectionTitle, sectionSubtitle, columns, dishes };
}
